import clsx from "clsx";
import { type ComponentProps, useMemo, useRef } from "react";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperCore } from "swiper/types";

import type { StrapiMedia } from "@shared/content-types/schemas/media.schema";
import type { ColorType } from "@shared/types";
import { BrandIcon } from "@shared/ui/brand-icon";
import type { IconNameType } from "@shared/ui/icon";
import { Image } from "@shared/ui/image";
import { Video } from "@shared/ui/video";
import { mod } from "@shared/utils";

import s from "./media-gallery-carousel.module.scss";

export type MediaGalleryCarouselOverlayIcon = {
  name: IconNameType;
  fillColor: ColorType;
};

export type MediaGalleryCarouselProps = ComponentProps<"div"> & {
  slides: StrapiMedia[];
  type?: "square" | "album";
  withControls?: boolean;
  overlayIcon?: MediaGalleryCarouselOverlayIcon;
};

type Slide = NonNullable<StrapiMedia> & {
  type?: "image" | "video";
  image?: { url: string; alternativeText?: string | null } | null;
  video?: { url: string } | null;
  url?: string;
  alternativeText?: string | null;
};

const getContent = (data: Slide) => {
  switch (data.type) {
    case "image":
      return (
        data.image && (
          <Image
            src={data.image.url}
            alt={data?.image.alternativeText || "slide"}
            className={s.slideImage}
          />
        )
      );

    case "video":
      return (
        data.video && (
          <Video src={data.video.url} autoPlay muted className={s.slideImage} />
        )
      );

    default:
      return (
        data.url && (
          <Image
            src={data.url}
            alt={data?.alternativeText || "slide"}
            className={s.slideImage}
          />
        )
      );
  }
};

export const MediaGalleryCarousel = (props: MediaGalleryCarouselProps) => {
  const { slides, type = "square", withControls = true, overlayIcon } = props;

  const $swiper = useRef<SwiperCore | null>(null);
  const $pagination = useRef<HTMLDivElement | null>(null);

  const safeSlides = useMemo(
    () => slides?.filter((slide): slide is Slide => Boolean(slide)) ?? [],
    [slides],
  );

  const mods = mod(s, { type });

  const handleInitSwiper = (swiper: SwiperCore) => {
    $swiper.current = swiper;
  };

  const handleMouseEnter = (idx: number) => {
    $swiper.current?.slideTo(idx);
  };

  const handleControlsEnter = () => {
    $swiper.current?.autoplay.stop();
  };

  const handleControlsLeave = () => {
    $swiper.current?.autoplay.start();
  };

  return (
    <div className={clsx(s.root, mods)}>
      {overlayIcon?.name && (
        <div className={s.overlayIconWrapper}>
          <BrandIcon
            className={s.overlayIcon}
            name={overlayIcon.name}
            fillColor={overlayIcon.fillColor}
            strokeColor="milk"
          />
          <BrandIcon
            className={clsx(s.overlayIcon, s.overlayIconArrow)}
            data-arrow-icon
            name="arrow"
            fillColor="milk"
            strokeColor={overlayIcon.fillColor}
          />
        </div>
      )}
      {withControls && safeSlides.length > 1 && (
        <div
          className={s.controls}
          onMouseEnter={handleControlsEnter}
          onMouseLeave={handleControlsLeave}
        >
          {safeSlides.map((slide, idx) => (
            <div
              key={`slide-${slide?.documentId ?? idx}`}
              onMouseMove={() => {
                handleMouseEnter(idx);
              }}
            ></div>
          ))}
        </div>
      )}
      <Swiper
        pagination={{
          type: "bullets",
          clickable: true,
          bulletClass: `${s.bullet}`,
          bulletActiveClass: `${s.bulletActive}`,
        }}
        modules={[Pagination, Autoplay]}
        className={s.slider}
        // loop
        autoplay={{
          delay: 2000,
          disableOnInteraction: false,
        }}
        onBeforeInit={(swiper) => {
          const pagination = swiper.params.pagination;
          if (pagination && typeof pagination !== "boolean") {
            pagination.el = $pagination.current;
          }
        }}
        onInit={handleInitSwiper}
      >
        {safeSlides.map((slide, idx) => (
          <SwiperSlide
            key={`slide-${slide?.documentId ?? idx}_${idx + 1}`}
            className={s.slide}
            // data-media-gallery
          >
            {getContent(slide)}
          </SwiperSlide>
        ))}
        {safeSlides.length > 1 && (
          <div ref={$pagination} className={s.pagination}></div>
        )}
      </Swiper>
    </div>
  );
};

MediaGalleryCarousel.displayName = "MediaGalleryCarousel";
